import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';

import UploadStep from './components/UploadStep';
import AnalyzeStep from './components/AnalyzeStep';
import ResultsStep from './components/ResultsStep';
import { createDenialAnalysis, getDenialAnalysis } from '../../utils/denialAnalysisApi';

const STEPS = [
  { label: 'Upload Denial Letter', description: 'Upload the denial letter PDF or image' },
  { label: 'Analyze', description: 'AI extracts and interprets the denial' },
  { label: 'Results', description: 'Review classification, codes, and next steps' },
];

const DenialAnalysisPage = () => {
  const navigate = useNavigate();
  const { analysisId } = useParams();

  const [activeStep, setActiveStep] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (analysisId) {
      loadAnalysis(analysisId);
    } else {
      initNewAnalysis();
    }
  }, [analysisId]); // eslint-disable-line react-hooks/exhaustive-deps

  const initNewAnalysis = async () => {
    try {
      setLoading(true);
      const response = await createDenialAnalysis();
      const created = response.data;
      setAnalysis(created);
      navigate(`/denial-analysis/${created.id}`, { replace: true });
    } catch (err) {
      setError('Failed to create new analysis session');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalysis = async (id) => {
    try {
      setLoading(true);
      const response = await getDenialAnalysis(id);
      const data = response.data;
      setAnalysis(data);

      // Restore step from status
      if (data.status === 'complete') {
        setActiveStep(2);
      } else if (['analyzing', 'ocr_complete', 'ocr_failed'].includes(data.status)) {
        setActiveStep(1);
      } else {
        setActiveStep(0);
      }
    } catch (err) {
      setError('Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (updated) => {
    setAnalysis(updated);
  };

  const handleUploadComplete = () => {
    setActiveStep(1);
  };

  const handleAnalysisComplete = (updated) => {
    setAnalysis(updated);
    setActiveStep(2);
  };

  if (loading && !analysis) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Analyze Denial Letter
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload a denial letter to get a structured analysis: denial type, codes, plain-English
        explanation, and prioritized next steps.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((step) => (
          <Step key={step.label}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 3 }}>
        {activeStep === 0 && analysis && (
          <UploadStep
            analysis={analysis}
            onNext={handleUploadComplete}
            onUpdate={handleUpdate}
          />
        )}
        {activeStep === 1 && analysis && (
          <AnalyzeStep
            analysis={analysis}
            onComplete={handleAnalysisComplete}
            onUpdate={handleUpdate}
          />
        )}
        {activeStep === 2 && analysis && (
          <ResultsStep
            result={analysis.result}
            analysis={analysis}
          />
        )}
      </Paper>
    </Container>
  );
};

export default DenialAnalysisPage;
